import type {
  BulkActionRequest,
  CreateLibraryItemRequest,
  FolderResponse,
  LibraryItemResponse,
  StorageUsageResponse,
  UpdateLibraryItemRequest,
} from "@poseref/shared";
import { apiFetch } from "./client.ts";

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

interface ItemsResponse {
  items: LibraryItemResponse[];
}

interface ItemResponse {
  item: LibraryItemResponse;
}

export async function fetchItems(params?: {
  folderId?: string;
  type?: string;
}): Promise<LibraryItemResponse[]> {
  const query = new URLSearchParams();
  if (params?.folderId) query.set("folder_id", params.folderId);
  if (params?.type) query.set("type", params.type);
  const qs = query.toString();
  const res = await apiFetch<ItemsResponse>(
    `/api/library/items${qs ? `?${qs}` : ""}`,
  );
  return res.items;
}

export async function fetchItem(id: string): Promise<LibraryItemResponse> {
  const res = await apiFetch<ItemResponse>(`/api/library/items/${id}`);
  return res.item;
}

export async function createItem(
  data: CreateLibraryItemRequest,
): Promise<LibraryItemResponse> {
  const res = await apiFetch<ItemResponse>("/api/library/items", {
    method: "POST",
    body: data,
  });
  return res.item;
}

export async function updateItem(
  id: string,
  data: UpdateLibraryItemRequest,
): Promise<LibraryItemResponse> {
  const res = await apiFetch<ItemResponse>(`/api/library/items/${id}`, {
    method: "PATCH",
    body: data,
  });
  return res.item;
}

export async function deleteItem(id: string): Promise<void> {
  await apiFetch(`/api/library/items/${id}`, { method: "DELETE" });
}

export async function bulkAction(data: BulkActionRequest): Promise<void> {
  await apiFetch("/api/library/items/bulk", { method: "POST", body: data });
}

export async function bulkDuplicate(
  ids: string[],
): Promise<LibraryItemResponse[]> {
  const res = await apiFetch<{ items: LibraryItemResponse[] }>(
    "/api/library/items/bulk",
    {
      method: "POST",
      body: { action: "duplicate", ids },
    },
  );
  return res.items;
}

/**
 * Find and delete cloud reference image items matching a given R2 file key.
 * Used during migration of cloud reference images to local IndexedDB storage.
 * Matches by checking if the presigned fileUrl contains the R2 key path.
 */
export async function deleteCloudReferenceByKey(r2Key: string): Promise<void> {
  const items = await fetchItems({ type: "reference_image" });
  // The fileUrl is a presigned URL that contains the R2 key as a path segment
  const match = items.find(
    (i) =>
      i.fileUrl?.includes(encodeURIComponent(r2Key)) ||
      i.fileUrl?.includes(r2Key),
  );
  if (match) await deleteItem(match.id);
}

// ---------------------------------------------------------------------------
// Storage usage
// ---------------------------------------------------------------------------

export async function fetchStorageUsage(): Promise<StorageUsageResponse> {
  return apiFetch<StorageUsageResponse>("/api/library/storage");
}

// ---------------------------------------------------------------------------
// Cleanup & backfill
// ---------------------------------------------------------------------------

export async function cleanup(): Promise<{
  migrated: number;
  removed: number;
  unfoldered: number;
}> {
  return apiFetch<{ migrated: number; removed: number; unfoldered: number }>(
    "/api/library/cleanup",
    {
      method: "POST",
    },
  );
}

export async function backfillSizes(): Promise<{ backfilled: number }> {
  return apiFetch<{ backfilled: number }>("/api/library/backfill-sizes", {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

interface FoldersResponse {
  folders: FolderResponse[];
}

interface FolderResponseSingle {
  folder: FolderResponse;
}

export async function fetchFolders(): Promise<FolderResponse[]> {
  const res = await apiFetch<FoldersResponse>("/api/library/folders");
  return res.folders;
}

export async function createFolder(name: string): Promise<FolderResponse> {
  const res = await apiFetch<FolderResponseSingle>("/api/library/folders", {
    method: "POST",
    body: { name },
  });
  return res.folder;
}

export async function updateFolder(
  id: string,
  data: { name?: string; sortOrder?: number },
): Promise<FolderResponse> {
  const res = await apiFetch<FolderResponseSingle>(
    `/api/library/folders/${id}`,
    {
      method: "PATCH",
      body: data,
    },
  );
  return res.folder;
}

export async function deleteFolder(id: string): Promise<void> {
  await apiFetch(`/api/library/folders/${id}`, { method: "DELETE" });
}
